import styled from 'styled-components';
import { ButtonBorderRadius } from '~/components/ui/Button';

const StyledListButton = styled.button`
	position: absolute;
	top: 50%;
	right: 55px;
	transform: translateY(-50%);
	background-color: transparent;
	border: 1px solid rgba(255, 255, 255, 0.2);
	width: auto;
	height: 32px;
	padding: 0 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${ButtonBorderRadius.Radius5}px;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background-color: rgba(255, 255, 255, 0.1);
		border-color: rgba(255, 255, 255, 0.4);
	}
`;

const ContentContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 20px;
	gap: 15px;
	overflow: visible;
`;

const StyledLayout = styled.div`
	display: flex;
	flex: 1;
	gap: 20px;
	overflow: hidden;
`;

const StyledLeftColumn = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 10px;
	overflow-y: auto;
	padding-right: 10px;
	min-width: 0;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: rgba(255, 255, 255, 0.05);
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.2);
		border-radius: 4px;
	}
`;

const StyledRightColumn = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 10px;
	overflow-y: auto;
	overflow-x: visible;
	padding-right: 10px;
	min-width: 0;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: rgba(255, 255, 255, 0.05);
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.2);
		border-radius: 4px;
	}
`;

const StyledSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	overflow: visible;
`;

const StyledSectionTitle = styled.div`
	font-family: 'GT Walsheim Pro';
	font-size: 18px;
	font-weight: 600;
	color: #ffffff;
	margin-bottom: 8px;
`;

const StyledInputGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const StyledInputLabel = styled.label`
	font-family: 'GT Walsheim Pro';
	font-size: 14px;
	color: rgba(255, 255, 255, 0.7);
`;

const StyledInputHint = styled.span`
	font-family: 'GT Walsheim Pro';
	font-size: 11px;
	color: rgba(255, 255, 255, 0.4);
	font-style: italic;
	margin-top: 2px;
`;

const StyledOptionalLabel = styled.span`
	font-family: 'GT Walsheim Pro';
	font-size: 10px;
	color: rgba(255, 255, 255, 0.35);
	margin-left: 6px;
`;

const StyledRewardCard = styled.div`
	position: relative;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: ${ButtonBorderRadius.Radius10}px;
	padding: 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	overflow: visible;
`;

const StyledRewardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
`;

const StyledRewardInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const StyledRewardDeleteButton = styled.button<{ $hover?: boolean }>`
	background-color: ${(props) => (props.$hover ? '#ffffff' : 'transparent')};
	border: none;
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	cursor: pointer;
	transition: background-color 0.3s ease;

	&::before {
		content: '';
		position: absolute;
		width: 12px;
		height: 2px;
		background-color: ${(props) =>
			props.$hover ? '#000000' : 'rgba(255, 255, 255, 0.5)'};
		transform: rotate(45deg);
		transition: background-color 0.3s ease;
	}

	&::after {
		content: '';
		position: absolute;
		width: 12px;
		height: 2px;
		background-color: ${(props) =>
			props.$hover ? '#000000' : 'rgba(255, 255, 255, 0.5)'};
		transform: rotate(-45deg);
		transition: background-color 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		&::before,
		&::after {
			background-color: #000000;
		}
	}
`;

const StyledNumberInput = styled.input`
	-webkit-appearance: none;
	-moz-appearance: textfield;
	appearance: none;

	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	&[type='number'] {
		-moz-appearance: textfield;
	}
`;

const StyledRewardFields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 8px;
`;

const StyledRewardActions = styled.div`
	display: flex;
	gap: 8px;
`;

const StyledButtonsRow = styled.div`
	display: flex;
	gap: 12px;
	margin-top: 16px;
	justify-content: flex-end;
`;

const StyledValidationError = styled.div`
	font-family: 'GT Walsheim Pro';
	font-size: 12px;
	color: #ff6b6b;
	margin-top: 4px;
`;

const StyledSuccessMessage = styled.div`
	position: absolute;
	top: 80px;
	left: 50%;
	transform: translateX(-50%);
	background: rgba(31, 128, 52, 0.9);
	padding: 12px 24px;
	border-radius: 8px;
	font-family: 'GT Walsheim Pro';
	font-size: 16px;
	color: #ffffff;
	animation: fadeIn 0.3s ease;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateX(-50%) translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateX(-50%) translateY(0);
		}
	}
`;

const StyledConfirmationOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.7);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 100;
`;

const StyledConfirmationModal = styled.div`
	background: rgba(30, 32, 35, 0.95);
	border: 1px solid rgba(255, 255, 255, 0.15);
	border-radius: 16px;
	padding: 24px;
	min-width: 350px;
	max-width: 450px;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const StyledConfirmationButtons = styled.div`
	display: flex;
	gap: 16px;
	width: 100%;
	justify-content: space-between;
`;

const StyledHintContainer = styled.div`
	position: absolute;
	bottom: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 5px;
	padding: 8px 12px;
	background-color: rgba(255, 255, 255, 0.95);
	border-radius: 8px;
	opacity: 0;
	transition: opacity 0.3s ease;
	white-space: nowrap;
	z-index: 1000;

	font-family: 'GT Walsheim Pro';
	font-weight: 400;
	font-size: 13px;
	color: #000000;

	&.hint-enter {
		opacity: 0;
	}

	&.hint-enter-active,
	&.hint-enter-done,
	&.hint-exit {
		opacity: 1;
	}

	&.hint-exit-active {
		opacity: 0;
	}
`;

const StyledRewardHintContainer = styled(StyledHintContainer)`
	bottom: auto;
	top: calc(100% + 8px);
	transform: translateX(-50%) translateY(-100%);
`;

const StyledRewardWrapper = styled.div`
	width: 100%;
`;

export {
	ContentContainer,
	StyledButtonsRow,
	StyledConfirmationButtons,
	StyledConfirmationModal,
	StyledConfirmationOverlay,
	StyledHintContainer,
	StyledInputGroup,
	StyledInputHint,
	StyledInputLabel,
	StyledLayout,
	StyledLeftColumn,
	StyledListButton,
	StyledNumberInput,
	StyledOptionalLabel,
	StyledRewardActions,
	StyledRewardCard,
	StyledRewardDeleteButton,
	StyledRewardFields,
	StyledRewardHeader,
	StyledRewardHintContainer,
	StyledRewardInfo,
	StyledRewardWrapper,
	StyledRightColumn,
	StyledSection,
	StyledSectionTitle,
	StyledSuccessMessage,
	StyledValidationError,
};
